import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  styled,
  alpha,
  useTheme,
} from '@mui/material';
import { Close, Email } from '@mui/icons-material';
import { api } from '../utils/api';

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface ForgotPasswordResponse {
  message: string;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.02),
    transition: 'all 0.3s ease',

    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.1),
      borderWidth: 2,
    },

    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.3),
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#FFFFFF',

      '& fieldset': {
        borderColor: theme.palette.mode === 'dark' ? '#313131' : theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },

  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,

    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.mode === 'dark' ? alpha('#E5E7EB', 0.7) : alpha(theme.palette.primary.main, 0.6),
  },
}));

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const response = await api.post<ForgotPasswordResponse>('/Authentication/ForgotPassword', {
        email,
      });

      if (response.message) {
        setMessage(response.message);
        setSuccess(true);
        setEmail('');
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Erro ao enviar email de recuperação');
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          background: theme.palette.mode === 'dark' ? '#1a1a1a' : '#FFFFFF',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          Recuperar Senha
        </Typography>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Digite seu email para receber instruções de recuperação de senha.
          </Typography>

          {message && (
            <Alert
              severity={success ? 'success' : 'error'}
              sx={{ mb: 2 }}
            >
              {message}
            </Alert>
          )}

          <StyledTextField
            autoFocus
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            disabled={isLoading}
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
            }}
            required
            sx={{ mb: 3 }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
            disabled={isLoading || !email}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Enviar Email'
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
